/**
 * MifeLife v2 — Mentor self-service dashboard (/mentor/panel).
 * Tabs: Profil · Uzmanlık · Deneyim · Eğitim · Fiyat · Müsaitlik · Ödeme.
 * Each tab saves via PATCH /api/v2/mentor/me. Shows live profile completion.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Loader2, ExternalLink, Save, User, Tag, Briefcase, GraduationCap,
  Wallet, Clock, BadgeDollarSign, LogOut, Sparkles, TrendingUp, Video, Calendar,
  Share2, Copy, Linkedin, Check, Calculator, Globe, CheckCircle2, Circle,
} from 'lucide-react';
import V2Footer from '../../components/v2/V2Footer';
import { apiRequest, getToken, removeToken } from '../../lib/utils';
import { useNoIndex } from '../../hooks/useNoIndex';
import { AddToCalendar } from '../../components/v2/SessionExtras';
import {
  SPECIALTY_OPTIONS, LANG_OPTIONS, Field, TextInput, TextArea, NumberInput,
  ChipMultiSelect, ListEditor, PricingEditor, AvailabilityEditor, CompletionBar,
  VacationToggle, BlockedDatesEditor, PhotoUpload,
} from '../../components/v2/MentorEditorFields';

const TABS = [
  { key: 'profil', label: 'Profil', icon: User },
  { key: 'seanslar', label: 'Seanslarım', icon: Video },
  { key: 'kazanc', label: 'Kazanç', icon: TrendingUp },
  { key: 'uzmanlik', label: 'Uzmanlık', icon: Tag },
  { key: 'deneyim', label: 'Deneyim', icon: Briefcase },
  { key: 'egitim', label: 'Eğitim', icon: GraduationCap },
  { key: 'fiyat', label: 'Fiyat', icon: BadgeDollarSign },
  { key: 'musaitlik', label: 'Müsaitlik', icon: Clock },
  { key: 'odeme', label: 'Ödeme', icon: Wallet },
];

export default function V2MentorPanelPage() {
  useNoIndex();
  const nav = useNavigate();
  const [mentor, setMentor] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [notMentor, setNotMentor] = useState(false);
  const [tab, setTab] = useState('profil');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!getToken()) { nav('/mentor/giris'); return; }
    apiRequest('/v2/mentor/me')
      .then((m) => { setMentor(m); setForm(m); })
      .catch((e) => {
        // Any failure to load the mentor profile means this account cannot act
        // as a mentor (403/404) — show the guard. 401 → re-login.
        if (e?.status === 401) {
          nav('/mentor/giris');
        } else if (e?.status === 403 || e?.status === 404 || (e?.message || '').toLowerCase().includes('mentör')) {
          setNotMentor(true);
        } else {
          toast.error(e?.message || 'Profil yüklenemedi');
          setNotMentor(true);
        }
      })
      .finally(() => setLoading(false));
  }, [nav]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const save = async (keys) => {
    setSaving(true);
    try {
      const payload = {};
      keys.forEach((k) => { payload[k] = form[k]; });
      const updated = await apiRequest('/v2/mentor/me', { method: 'PATCH', body: JSON.stringify(payload) });
      setMentor(updated); setForm(updated);
      toast.success('Kaydedildi');
    } catch (e) {
      toast.error(e?.message || 'Kaydedilemedi');
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#0F0F1A] flex items-center justify-center"><Loader2 className="w-7 h-7 text-violet-300 animate-spin" /></div>;
  }
  if (notMentor) {
    return (
      <div className="min-h-screen bg-[#0F0F1A] text-white flex flex-col items-center justify-center px-6 text-center" data-testid="v2-mentor-not-mentor">
        <Sparkles className="w-8 h-8 text-violet-300 mb-4" />
        <h1 className="text-2xl font-semibold">Bu hesap bir mentör hesabı değil</h1>
        <p className="mt-2 text-sm text-slate-400 max-w-sm">Mentör paneline yalnızca onaylanmış mentörler erişebilir. Mentör olmak istersen başvur, onaylandığında davet e-postası alırsın.</p>
        <div className="mt-6 flex gap-3">
          <Link to="/mentor-basvuru" className="h-10 px-5 rounded-lg bg-violet-500 text-white text-sm font-semibold flex items-center" data-testid="v2-not-mentor-apply">Mentör Ol</Link>
          <Link to="/" className="h-10 px-5 rounded-lg border border-white/15 text-slate-200 text-sm flex items-center">Ana sayfa</Link>
        </div>
      </div>
    );
  }

  const slug = mentor?.slug || mentor?.id;

  // Defensive: never render the editor with a null mentor (race guard).
  if (!mentor) {
    return <div className="min-h-screen bg-[#0F0F1A] flex items-center justify-center"><Loader2 className="w-7 h-7 text-violet-300 animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0F0F1A] via-[#11111E] to-[#1A1A2E] text-white antialiased" data-testid="v2-mentor-panel">
      {/* Top bar */}
      <header className="border-b border-white/[0.06] bg-[#0F0F1A]/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="text-sm font-semibold tracking-tight">MifeLife <span className="text-violet-300">Mentör</span></Link>
          <div className="flex items-center gap-3">
            <Link to={`/m/${slug}`} target="_blank" className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-white transition" data-testid="v2-panel-preview">
              <ExternalLink className="w-3.5 h-3.5" /> Profili önizle
            </Link>
            <button onClick={() => { removeToken(); nav('/mentor/giris'); }} className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-rose-300 transition" data-testid="v2-panel-logout">
              <LogOut className="w-3.5 h-3.5" /> Çıkış
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 grid lg:grid-cols-12 gap-6">
        {/* Sidebar */}
        <aside className="lg:col-span-3 space-y-4">
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <div className="flex items-center gap-3 mb-4">
              <img src={mentor.photo_url} alt={mentor.name} className="w-12 h-12 rounded-full object-cover" />
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{mentor.name}</div>
                <div className="text-xs text-slate-400 truncate">{mentor.title}</div>
              </div>
            </div>
            <CompletionBar value={mentor.profile_completion ?? 0} />
            {!mentor.onboarding_completed && (
              <Link to="/mentor/onboarding" className="mt-4 block text-center h-9 leading-9 rounded-lg bg-violet-500/15 border border-violet-400/30 text-violet-100 text-xs font-medium hover:bg-violet-500/25 transition" data-testid="v2-panel-onboarding-cta">
                Onboarding'i tamamla →
              </Link>
            )}
          </div>
          <ShareProfileCard mentor={mentor} />
          <OnboardingChecklist checklist={mentor.checklist} onGoTab={setTab} />
          <nav className="rounded-2xl border border-white/10 bg-white/[0.02] p-2">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`w-full flex items-center gap-2.5 px-3 h-10 rounded-lg text-sm transition ${tab === t.key ? 'bg-violet-500/15 text-white' : 'text-slate-400 hover:text-white hover:bg-white/[0.04]'}`}
                  data-testid={`v2-panel-tab-${t.key}`}
                >
                  <Icon className="w-4 h-4" /> {t.label}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Content */}
        <main className="lg:col-span-9">
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid={`v2-panel-section-${tab}`}>
            {tab === 'profil' && (
              <Section title="Profil bilgileri" onSave={() => save(['name', 'title', 'photo_url', 'city', 'languages', 'bio_short', 'bio_full', 'session_about', 'linkedin_url', 'website_url'])} saving={saving}>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Ad Soyad"><TextInput value={form.name} onChange={(v) => set('name', v)} testid="mentor-field-name" /></Field>
                  <Field label="Ünvan"><TextInput value={form.title} onChange={(v) => set('title', v)} placeholder="örn. Senior Ürün Mentoru" testid="mentor-field-title" /></Field>
                  <Field label="Şehir"><TextInput value={form.city} onChange={(v) => set('city', v)} placeholder="İstanbul" testid="mentor-field-city" /></Field>
                  <Field label="LinkedIn URL"><TextInput value={form.linkedin_url} onChange={(v) => set('linkedin_url', v)} placeholder="https://linkedin.com/in/..." testid="mentor-field-linkedin" /></Field>
                  <Field label="Website"><TextInput value={form.website_url} onChange={(v) => set('website_url', v)} placeholder="https://..." testid="mentor-field-website" /></Field>
                </div>
                <Field label="Profil fotoğrafı" hint="Gerçek, profesyonel bir portre — JPG/PNG/WEBP"><PhotoUpload value={form.photo_url} onChange={(v) => set('photo_url', v)} testidPrefix="mentor-field-photo" /></Field>
                <Field label="Diller"><ChipMultiSelect options={LANG_OPTIONS} selected={form.languages || []} onChange={(v) => set('languages', v)} getKey={(o) => o.code} getLabel={(o) => o.label} testidPrefix="mentor-lang" /></Field>
                <Field label="Kısa biyografi" hint="Listede görünür (40+ karakter)"><TextArea value={form.bio_short} onChange={(v) => set('bio_short', v)} rows={2} testid="mentor-field-bio-short" /></Field>
                <Field label="Detaylı biyografi" hint="Profil sayfasında görünür (150+ karakter)"><TextArea value={form.bio_full} onChange={(v) => set('bio_full', v)} rows={5} testid="mentor-field-bio-full" /></Field>
                <Field label="Görüşmede ne konuşulur?"><TextArea value={form.session_about} onChange={(v) => set('session_about', v)} rows={3} testid="mentor-field-session-about" /></Field>
              </Section>
            )}
            {tab === 'kazanc' && <EarningsPanel />}
            {tab === 'seanslar' && <SessionsPanel />}
            {tab === 'uzmanlik' && (
              <Section title="Uzmanlık alanları" onSave={() => save(['specialties'])} saving={saving}>
                <Field label="Alanlarını seç (2+ önerilir)"><ChipMultiSelect options={SPECIALTY_OPTIONS} selected={form.specialties || []} onChange={(v) => set('specialties', v)} testidPrefix="mentor-spec" /></Field>
              </Section>
            )}
            {tab === 'deneyim' && (
              <Section title="İş deneyimi" onSave={() => save(['experience'])} saving={saving}>
                <ListEditor items={form.experience || []} onChange={(v) => set('experience', v)} addLabel="Deneyim ekle" testid="mentor-experience"
                  fields={[{ key: 'company', placeholder: 'Şirket' }, { key: 'role', placeholder: 'Pozisyon' }, { key: 'period', placeholder: '2018 - Bugün' }]} />
              </Section>
            )}
            {tab === 'egitim' && (
              <Section title="Eğitim" onSave={() => save(['education'])} saving={saving}>
                <ListEditor items={form.education || []} onChange={(v) => set('education', v)} addLabel="Eğitim ekle" testid="mentor-education"
                  fields={[{ key: 'school', placeholder: 'Okul' }, { key: 'degree', placeholder: 'Derece / Bölüm' }, { key: 'period', placeholder: '2010 - 2014' }]} />
              </Section>
            )}
            {tab === 'fiyat' && (
              <Section title="Seans fiyatlandırması" onSave={() => save(['pricing', 'hourly_rate_try'])} saving={saving}>
                <Field label="Saatlik referans ücret (₺)"><NumberInput value={form.hourly_rate_try} onChange={(v) => set('hourly_rate_try', v)} testid="mentor-field-hourly" /></Field>
                <Field label="Süreye göre fiyat (kendi fiyatını belirle)"><PricingEditor pricing={form.pricing || []} onChange={(v) => set('pricing', v)} /></Field>
                <NetEarningsWidget mentor={mentor} defaultFee={form.hourly_rate_try} />
              </Section>
            )}
            {tab === 'musaitlik' && (
              <Section title="Haftalık müsaitlik" onSave={() => save(['available_hours', 'vacation_mode', 'blocked_dates'])} saving={saving}>
                <VacationToggle value={!!form.vacation_mode} onChange={(v) => set('vacation_mode', v)} />
                <Field label="Görüşmeye açık saatler"><AvailabilityEditor availableHours={form.available_hours || {}} onChange={(v) => set('available_hours', v)} /></Field>
                <Field label="Kapalı günler" hint="Bu tarihlerde rezervasyon alınmaz."><BlockedDatesEditor dates={form.blocked_dates || []} onChange={(v) => set('blocked_dates', v)} /></Field>
              </Section>
            )}
            {tab === 'odeme' && (
              <Section title="Ödeme bilgileri" onSave={() => save(['iban', 'tax_no'])} saving={saving}>
                <p className="text-xs text-slate-500 mb-2">Kazançların bu IBAN'a ödenir. Bilgilerin yalnızca yönetim tarafından görülür.</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="IBAN"><TextInput value={form.iban} onChange={(v) => set('iban', v)} placeholder="TR.." testid="mentor-field-iban" /></Field>
                  <Field label="TC Kimlik / Vergi No"><TextInput value={form.tax_no} onChange={(v) => set('tax_no', v)} testid="mentor-field-tax" /></Field>
                </div>
              </Section>
            )}
          </div>
        </main>
      </div>
      <V2Footer lang="tr" />
    </div>
  );
}

const Section = ({ title, children, onSave, saving }) => (
  <div className="space-y-5">
    <div className="flex items-center justify-between">
      <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
      <button onClick={onSave} disabled={saving} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-gradient-to-r from-violet-500 to-violet-600 text-white text-xs font-semibold hover:from-violet-400 hover:to-violet-500 disabled:opacity-50 transition" data-testid="v2-panel-save">
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Kaydet
      </button>
    </div>
    {children}
  </div>
);

const fmtTRY = (n) => `₺${Number(n || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}`;

const LEDGER_STATUS = {
  pending:            { label: 'Bekliyor', cls: 'bg-amber-500/15 text-amber-300 border-amber-400/30' },
  under_review:       { label: 'İncelemede', cls: 'bg-orange-500/15 text-orange-300 border-orange-400/30' },
  available:          { label: 'Ödenebilir', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30' },
  paid_out:           { label: 'Ödendi', cls: 'bg-sky-500/15 text-sky-300 border-sky-400/30' },
  reversed:           { label: 'İade', cls: 'bg-rose-500/15 text-rose-300 border-rose-400/30' },
  partially_reversed: { label: 'Kısmi İade', cls: 'bg-rose-500/10 text-rose-200 border-rose-400/20' },
};

const EarningsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    apiRequest('/v2/mentor/earnings')
      .then(setData)
      .catch((e) => toast.error(e?.message || 'Kazançlar yüklenemedi'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-violet-300 animate-spin" /></div>;
  const t = data?.totals || {};
  const entries = data?.entries || [];
  const payout = data?.payout || {};

  return (
    <div className="space-y-6" data-testid="v2-mentor-earnings">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold tracking-tight">Kazançlarım</h2>
        {!data?.iban_valid && (
          <span className="text-[11px] text-amber-300 inline-flex items-center gap-1.5" data-testid="v2-earn-iban-warning">
            <Wallet className="w-3.5 h-3.5" /> {data?.iban_set ? 'IBAN doğrulanmadı (TR formatı)' : 'Ödeme için IBAN ekle'} →
          </span>
        )}
      </div>

      {/* Headline cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <EarnCard label="Toplam net kazanç" value={fmtTRY(t.net)} accent="emerald" testid="v2-earn-total" />
        <EarnCard label="Bu ay" value={fmtTRY(t.this_month_net)} testid="v2-earn-month" />
        <EarnCard label="Ödenebilir" value={fmtTRY(t.available_net)} accent="emerald" testid="v2-earn-available" />
        <EarnCard label="Bekleyen (72s)" value={fmtTRY(t.pending_net)} testid="v2-earn-pending" />
      </div>

      {/* 5-state ledger breakdown */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4" data-testid="v2-earn-states">
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-3">Kazanç durumları</div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <StateChip label="Bekliyor" value={fmtTRY(t.pending_net)} cls="text-amber-300" testid="v2-state-pending" />
          <StateChip label="İncelemede" value={fmtTRY(t.under_review_net)} cls="text-orange-300" testid="v2-state-under-review" />
          <StateChip label="Ödenebilir" value={fmtTRY(t.available_net)} cls="text-emerald-300" testid="v2-state-available" />
          <StateChip label="Ödendi" value={fmtTRY(t.paid_out_net)} cls="text-sky-300" testid="v2-state-paid-out" />
          <StateChip label="İade" value={fmtTRY(t.refunded_total)} cls="text-rose-300" sub={`${t.reversed_count || 0} tam / ${t.partially_reversed_count || 0} kısmi`} testid="v2-state-reversed" />
        </div>
      </div>

      {/* Payout eligibility */}
      <div className={`rounded-2xl border p-4 ${payout.eligible ? 'border-emerald-400/20 bg-emerald-500/[0.04]' : 'border-white/10 bg-white/[0.02]'}`} data-testid="v2-payout-status">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-sm font-medium">Ödeme durumu</span>
          <span className={`text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full border ${payout.eligible ? 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30' : 'bg-amber-500/15 text-amber-300 border-amber-400/30'}`}>
            {payout.eligible ? 'Ödemeye uygun' : 'Beklemede'}
          </span>
        </div>
        {payout.eligible ? (
          <p className="text-xs text-emerald-300/90">Ödenebilir bakiyen ({fmtTRY(payout.available_net)}) bir sonraki manuel payout partisinde IBAN'ına gönderilecek.</p>
        ) : (
          <ul className="text-xs text-slate-400 space-y-1 mt-1">
            {(payout.blockers || []).map((b, i) => <li key={i} className="flex items-center gap-1.5"><span className="text-amber-400">•</span> {b}</li>)}
          </ul>
        )}
      </div>

      <p className="text-xs text-slate-500">
        Seans bedelinden platform komisyonu düşülür; net tutar IBAN'ına ödenir. Tutarlar seans bitiminden <strong className="text-slate-400">72 saat</strong> sonra "ödenebilir" olur. Açık bir itiraz varsa tutar "incelemede" tutulur.
      </p>

      {/* Ledger */}
      {entries.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center text-sm text-slate-400" data-testid="v2-earn-empty">
          Henüz kazanç kaydın yok. İlk ödenen seansından sonra burada görünür.
        </div>
      ) : (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-x-auto" data-testid="v2-earn-ledger">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
                {['Tarih', 'Süre', 'Bedel', 'Komisyon', 'Net', 'Durum'].map((h) => <th key={h} className="text-left font-medium px-4 py-3">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => {
                const st = LEDGER_STATUS[e.status] || { label: e.status, cls: 'bg-white/[0.05] text-slate-400 border-white/10' };
                return (
                  <tr key={e.id} className="border-t border-white/[0.04]" data-testid={`v2-earn-row-${e.id}`}>
                    <td className="px-4 py-3">{e.session_scheduled_at ? new Date(e.session_scheduled_at).toLocaleDateString('tr-TR') : '—'}</td>
                    <td className="px-4 py-3 tabular-nums">{e.duration_min}dk</td>
                    <td className="px-4 py-3 tabular-nums">{fmtTRY(e.gross_try)}</td>
                    <td className="px-4 py-3 tabular-nums text-amber-300">−{fmtTRY(e.commission_try)} <span className="text-slate-600 text-xs">({e.commission_pct}%)</span></td>
                    <td className="px-4 py-3 tabular-nums text-emerald-300 font-medium">{fmtTRY(e.net_try)}</td>
                    <td className="px-4 py-3"><span className={`text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const EarnCard = ({ label, value, accent, testid }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-1.5">{label}</div>
    <div className={`text-xl font-semibold tabular-nums ${accent === 'emerald' ? 'text-emerald-300' : 'text-white'}`}>{value}</div>
  </div>
);

const StateChip = ({ label, value, cls, sub, testid }) => (
  <div className="rounded-xl border border-white/[0.06] bg-[#0a0a14] px-3 py-2.5" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-1">{label}</div>
    <div className={`text-base font-semibold tabular-nums ${cls}`}>{value}</div>
    {sub && <div className="text-[10px] text-slate-600 mt-0.5">{sub}</div>}
  </div>
);

const TRANSLATION_BETA_FEE = 0; // Beta süresince canlı çeviri platform tarafından karşılanır.

const NetLine = ({ label, value, neg, muted }) => (
  <div className="flex items-center justify-between">
    <span className="text-slate-400">{label}</span>
    <span className={`tabular-nums ${neg ? 'text-amber-300' : muted ? 'text-slate-500' : 'text-white'}`}>{value}</span>
  </div>
);

const NetEarningsWidget = ({ mentor, defaultFee }) => {
  const commissionPct = Number(mentor?.commission_pct ?? 30);
  const [fee, setFee] = useState(Number(defaultFee) || Number(mentor?.hourly_rate_try) || 1000);
  const [translation, setTranslation] = useState(false);
  const f = Number(fee) || 0;
  const commission = Math.round((f * commissionPct) / 100);
  const translationCost = translation ? TRANSLATION_BETA_FEE : 0;
  const net = Math.max(0, f - commission - translationCost);
  return (
    <div className="rounded-2xl border border-emerald-400/20 bg-gradient-to-br from-emerald-500/[0.06] to-teal-500/[0.03] p-5 mt-2" data-testid="v2-net-earnings">
      <div className="flex items-center gap-2 mb-3">
        <Calculator className="w-4 h-4 text-emerald-300" />
        <span className="text-sm font-semibold tracking-tight">Tahmini Net Kazancın</span>
      </div>
      <label className="block mb-4">
        <span className="text-[11px] uppercase tracking-[0.14em] text-slate-500">Seans ücreti (₺)</span>
        <input type="number" min="0" value={fee} onChange={(e) => setFee(e.target.value)} data-testid="v2-net-fee"
          className="mt-1 w-full h-10 rounded-lg bg-[#0a0a14] border border-white/15 text-white px-3 focus:outline-none focus:border-emerald-400/60" />
      </label>
      <label className="flex items-center justify-between gap-3 mb-4 cursor-pointer">
        <span className="text-sm text-slate-300 flex items-center gap-1.5"><Globe className="w-3.5 h-3.5 text-fuchsia-300" /> Canlı çeviri (Global Mentoring Beta)</span>
        <input type="checkbox" checked={translation} onChange={(e) => setTranslation(e.target.checked)} data-testid="v2-net-translation" className="w-4 h-4 accent-emerald-500" />
      </label>
      <div className="space-y-2 text-sm border-t border-white/[0.06] pt-3">
        <NetLine label="Seans ücreti" value={`₺${f.toLocaleString('tr-TR')}`} />
        <NetLine label={`Platform komisyonu (%${commissionPct})`} value={`−₺${commission.toLocaleString('tr-TR')}`} neg />
        {translation && <NetLine label="Çeviri hizmeti (Beta · platform karşılar)" value="₺0" muted />}
      </div>
      <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
        <span className="text-sm text-slate-300">Tahmini net kazancın</span>
        <span className="text-2xl font-bold text-emerald-300 tabular-nums" data-testid="v2-net-result">₺{net.toLocaleString('tr-TR')}</span>
      </div>
      <p className="mt-2 text-[11px] text-slate-500">Brüt hakediş; vergi (stopaj/KDV) hariç. Beta süresince canlı çeviri ücretsizdir.</p>
    </div>
  );
};

const ShareProfileCard = ({ mentor }) => {
  const [copied, setCopied] = useState('');
  const slug = mentor.slug || mentor.id;
  const url = `${window.location.origin}/m/${slug}`;
  const shareUrl = `${window.location.origin}/api/v2/mentors/${slug}/share`;
  const shareText = `🚀 Artık MifeLife'ta mentorluk veriyorum!${mentor.title ? ` ${mentor.title} olarak` : ''} birebir online görüşmelerle deneyimimi paylaşıyorum. Hedeflerine birlikte bakalım — randevu almak için profilime göz at:\n${shareUrl}\n\n#mentorluk #MifeLife #kariyer`;
  const pingShared = () => { apiRequest('/v2/mentor/share-event', { method: 'POST' }).catch(() => {}); };
  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key); toast.success('Kopyalandı');
      if (key === 'text') pingShared();
      setTimeout(() => setCopied(''), 1800);
    } catch (_) { toast.error('Kopyalanamadı'); }
  };
  const linkedinShare = () => {
    pingShared();
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank', 'noopener,noreferrer');
  };
  return (
    <div className="rounded-2xl border border-violet-400/20 bg-gradient-to-br from-violet-500/[0.06] to-fuchsia-500/[0.03] p-5" data-testid="v2-share-profile">
      <div className="flex items-center gap-2 mb-1">
        <Share2 className="w-4 h-4 text-fuchsia-300" />
        <span className="text-sm font-semibold tracking-tight">Profilini paylaş</span>
      </div>
      <p className="text-xs text-slate-400 mb-3">Ağına duyur, daha çok danışana ulaş. Sosyal medyada otomatik profesyonel önizleme görünür.</p>
      <div className="flex items-center gap-2 rounded-lg bg-[#0a0a14] border border-white/10 px-2.5 py-2 mb-2">
        <span className="text-[11px] text-slate-300 truncate flex-1" data-testid="v2-share-url">{url}</span>
        <button onClick={() => copy(url, 'url')} className="flex-shrink-0 text-slate-400 hover:text-white transition" title="Linki kopyala" data-testid="v2-share-copy-link">
          {copied === 'url' ? <Check className="w-3.5 h-3.5 text-emerald-300" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>
      <a href={shareUrl} target="_blank" rel="noopener noreferrer" className="block text-[11px] text-violet-300 hover:text-white transition mb-2.5" data-testid="v2-share-preview-link">
        Sosyal önizlemeyi gör →
      </a>
      <button onClick={linkedinShare} className="w-full inline-flex items-center justify-center gap-2 h-9 rounded-lg bg-[#0A66C2] text-white text-xs font-semibold hover:bg-[#0a5aad] transition" data-testid="v2-share-linkedin">
        <Linkedin className="w-3.5 h-3.5" /> LinkedIn'de paylaş
      </button>
      <button onClick={() => copy(shareText, 'text')} className="w-full mt-2 inline-flex items-center justify-center gap-2 h-9 rounded-lg border border-white/15 text-slate-200 text-xs font-medium hover:bg-white/[0.04] transition" data-testid="v2-share-copy-text">
        {copied === 'text' ? <Check className="w-3.5 h-3.5 text-emerald-300" /> : <Copy className="w-3.5 h-3.5" />} Paylaşım metnini kopyala
      </button>
    </div>
  );
};

const OnboardingChecklist = ({ checklist, onGoTab }) => {
  if (!checklist) return null;
  const { items = [], completion = 0, launch_ready, missing_for_launch = [], classification } = checklist;
  const CLS = {
    launch_ready: { label: 'Yayına hazır', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30' },
    almost_ready: { label: 'Neredeyse hazır', cls: 'bg-amber-500/15 text-amber-300 border-amber-400/30' },
    needs_work: { label: 'Eksikler var', cls: 'bg-rose-500/15 text-rose-300 border-rose-400/30' },
  };
  const badge = CLS[classification] || CLS.needs_work;
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="v2-onboarding-checklist">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold tracking-tight">Profil checklist</span>
        <span className={`text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${badge.cls}`} data-testid="v2-checklist-status">{badge.label}</span>
      </div>
      <ul className="space-y-1.5">
        {items.map((it) => (
          <li key={it.key}>
            <button
              onClick={() => onGoTab(it.tab)}
              className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-left hover:bg-white/[0.04] transition group"
              data-testid={`v2-checklist-item-${it.key}`}
            >
              {it.done
                ? <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                : <Circle className="w-4 h-4 text-slate-600 flex-shrink-0 group-hover:text-violet-300" />}
              <span className={`text-xs flex-1 ${it.done ? 'text-slate-400 line-through' : 'text-slate-200'}`}>{it.label}</span>
              {!it.required_for_launch && <span className="text-[9px] text-slate-500 uppercase tracking-wide">ops.</span>}
            </button>
          </li>
        ))}
      </ul>
      {!launch_ready && missing_for_launch.length > 0 && (
        <p className="mt-3 text-[11px] text-amber-200/80 leading-relaxed" data-testid="v2-checklist-missing">
          Yayın için eksik: {missing_for_launch.join(', ')}.
        </p>
      )}
      {launch_ready && (
        <p className="mt-3 text-[11px] text-emerald-300/90" data-testid="v2-checklist-ready">
          Profilin yayına hazır 🎉 Aramalarda ve önerilerde görünüyorsun.
        </p>
      )}
    </div>
  );
};

const SESS_STATUS = {
  paid: { label: 'Ödendi · Onaylı', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30' },
  completed: { label: 'Tamamlandı', cls: 'bg-sky-500/15 text-sky-300 border-sky-400/30' },
};

const SessionRow = ({ s, joinable }) => {
  const st = SESS_STATUS[s.status] || { label: s.status, cls: 'bg-white/[0.05] text-slate-400 border-white/10' };
  return (
    <tr className="border-t border-white/[0.04]" data-testid={`v2-msession-row-${s.id}`}>
      <td className="px-4 py-3 text-white">{s.mentee_name || s.user_name || 'Danışan'}</td>
      <td className="px-4 py-3 tabular-nums">{new Date(s.scheduled_at).toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' })}</td>
      <td className="px-4 py-3 tabular-nums">{s.duration_min}dk</td>
      <td className="px-4 py-3 tabular-nums">₺{s.price_try}</td>
      <td className="px-4 py-3"><span className={`text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span></td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2 justify-end">
          {joinable && (
            <Link to={`/app/seans/${s.id}`} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-violet-500 text-white text-xs font-semibold hover:bg-violet-400 transition" data-testid={`v2-msession-join-${s.id}`}>
              <Video className="w-3.5 h-3.5" /> Görüşmeye katıl
            </Link>
          )}
          <AddToCalendar sessionId={s.id} compact />
        </div>
      </td>
    </tr>
  );
};

const SessionsTable = ({ rows, joinable, emptyText }) => {
  if (!rows.length) {
    return <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 text-center text-sm text-slate-400">{emptyText}</div>;
  }
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
            {['Danışan', 'Tarih / Saat', 'Süre', 'Ücret', 'Durum', ''].map((h, i) => <th key={i} className={`font-medium px-4 py-3 ${i === 5 ? 'text-right' : 'text-left'}`}>{h}</th>)}
          </tr>
        </thead>
        <tbody>{rows.map((s) => <SessionRow key={s.id} s={s} joinable={joinable} />)}</tbody>
      </table>
    </div>
  );
};

const SessionsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    apiRequest('/v2/mentor/sessions')
      .then(setData)
      .catch((e) => toast.error(e?.message || 'Seanslar yüklenemedi'))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-violet-300 animate-spin" /></div>;
  const upcoming = data?.upcoming || [];
  const past = data?.past || [];
  return (
    <div className="space-y-6" data-testid="v2-mentor-sessions">
      <div className="flex items-center gap-2">
        <Calendar className="w-4 h-4 text-violet-300" />
        <h2 className="text-lg font-semibold tracking-tight">Seanslarım</h2>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-2">Yaklaşan seanslar ({upcoming.length})</div>
        <SessionsTable rows={upcoming} joinable emptyText="Yaklaşan seansın yok. Profilini tamamla ve müsaitlik gir; rezervasyonlar burada görünür." />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-2">Geçmiş seanslar ({past.length})</div>
        <SessionsTable rows={past} joinable={false} emptyText="Henüz tamamlanmış seansın yok." />
      </div>
      <p className="text-xs text-slate-500">Görüşme odası, seans saatinden 15 dakika önce "Görüşmeye katıl" ile açılır.</p>
    </div>
  );
};
